import { useMutation, useQuery } from "@tanstack/react-query";

import { FilesystemAPI } from "@/features/caseFiles/api/filesystemApi";
import type { FileCard, FileCardStatus } from "@/features/caseFiles/types";
import { queryClient } from "@/lib/query/queryClient";

/** How often a card being built is checked on. */
export const CARD_POLL_MS = 3_000;
/** Checks before a row stops watching (the build mark lapses after 15 minutes). */
export const CARD_MAX_POLLS = 200;

export const fileCardQueryKey = (individualId: string, nodeId: string) =>
  ["case-file-card", individualId, nodeId] as const;

export const isCardBuilding = (status: FileCardStatus | undefined) =>
  status === "queued" || status === "building";

/**
 * A file's card, fetched only while `enabled` (the viewer is open or the row
 * started a build), and checked every few seconds while it is being built.
 */
export const useFileCard = (
  individualId: string,
  nodeId: string,
  enabled: boolean
) =>
  useQuery(
    {
      queryKey: fileCardQueryKey(individualId, nodeId),
      queryFn: () => FilesystemAPI.getFileCard(individualId, nodeId),
      enabled: enabled && Boolean(individualId),
      refetchInterval: (query) =>
        isCardBuilding(query.state.data?.status) &&
        query.state.dataUpdateCount < CARD_MAX_POLLS
          ? CARD_POLL_MS
          : false,
      refetchOnWindowFocus: false
    },
    queryClient
  );

/** Start building a file's card; the answer seeds the card's query. */
export const useRequestFileCard = (individualId: string, nodeId: string) =>
  useMutation(
    {
      mutationFn: () => FilesystemAPI.requestFileCard(individualId, nodeId),
      onSuccess: (card) =>
        queryClient.setQueryData<FileCard>(
          fileCardQueryKey(individualId, nodeId),
          card
        )
    },
    queryClient
  );

/**
 * The card's markdown for reading: its header lines (title, path, what the
 * document is, the summary) each become their own paragraph; list items stay
 * one list.
 */
export const cardDisplayMarkdown = (markdown: string) =>
  markdown.replace(/(?<!\n)\n(?!\n|- )/g, "\n\n");

export const unavailableMessage = (card: FileCard) =>
  card.reason === "internal"
    ? "Internal files don't get a card."
    : "This file's text isn't ready yet. Try again once it's been read.";
